
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import draggable from "vuedraggable";

import { Media } from "@/types/media";
import {
  Control,
  decorateValueEmittedByInputWithMetadata,
  RepeaterOptions
} from "@/types/controls";
import defensiveCopy from "@/util/defensive-copy";

import ControlsTree from "@/components/Reusable/ControlsTree/ControlsTree.vue";
import RepeaterItemMedia from "@/components/Reusable/CustomInputs/Repeater/RepeaterItemMedia.vue";
import RepeaterItemTitleText from "@/components/Reusable/CustomInputs/Repeater/RepeaterItemTitleText.vue";
import RepeaterItemAccordion from "@/components/Reusable/CustomInputs/Repeater/RepeaterItemAccordion.vue";
import RepeaterItemPopup from "@/components/Reusable/CustomInputs/Repeater/RepeaterItemPopup.vue";
import MediaUploadDialog from "@/components/Reusable/CustomInputs/MediaInput/MediaUploadDialog.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import Dialog from "@/components/UI/Dialog.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import IconButton from "@/components/UI/IconButton.vue";
import { renderRepeaterVariables } from "@/util/repeaters";
import { uiStore } from "@/store/store-accessor";

@Component({
  components: {
    IconButton,
    NiceButton,
    draggable,
    ControlsTree,
    RepeaterItemPopup,
    RepeaterItemMedia,
    RepeaterItemAccordion,
    RepeaterItemTitleText,
    MediaUploadDialog,
    Dialog
  }
})
export default class Repeater extends Vue {
  @Prop()
  control!: Control;

  @Prop()
  path!: string;

  @Prop({ default: false })
  createSnapshotWhenItemsListChanges!: boolean;

  @Prop({
    default() {
      return [];
    }
  })
  value!: unknown[];

  dragOptions = {
    animation: 200,
    handle: ".breakdance-repeater-item-handler",
    filter: ".breakdance-repeater-item--ignore-drag",
    ghostClass: "breakdance-repeater-item--ghost",
    forceFallback: true
  };

  expandedIndex = -1;
  indexToDelete = -1;
  dialogOpen = false;
  deleteDialogOpen = false;

  @Watch("expandedIndex")
  onExpandedIndexChange() {
    if (this.options.popupMode) {
      uiStore.setPanelState({
        ...uiStore.panelState,
        oxyPanelIsMasked: this.expandedIndex !== -1
      });
    }
  }

  get options(): RepeaterOptions | Record<string, never> {
    return this.control?.options.repeaterOptions || {};
  }

  get titleTemplate() {
    return this.options?.titleTemplate;
  }

  get defaultTitle() {
    return this.options?.defaultTitle || "Item";
  }

  get buttonName() {
    return this.options?.buttonName || "Add Item";
  }

  get defaultNewValue() {
    return this.options?.defaultNewValue || [];
  }

  get duplicateNewValue() {
    return this.options?.duplicateNewValue || {};
  }

  get deleteText() {
    return this.options?.deleteText || "Are you sure you want to delete this?";
  }

  get noDuplicate() {
    return this.options?.noDuplicate;
  }

  processDefaultValue(newValue: unknown) {
    return renderRepeaterVariables(newValue, this.value?.length || 0);
  }

  toggleExpandedItem(indexToExpand: number) {
    if (this.expandedIndex === indexToExpand) {
      this.expandedIndex = -1;
    } else {
      this.expandedIndex = indexToExpand;
    }
  }

  forceCollapse() {
    // we call forceCollapse before drag
    // but it would be better to not collapse before drag
    // but if we dont, expandedIndex is wrong
    // really, we should have a RepeaterItem component that holds its own collapse/expanded state. lol.
    this.expandedIndex = -1;
  }

  emit(newValue: unknown) {
    this.$emit(
      "input",
      this.createSnapshotWhenItemsListChanges
        ? decorateValueEmittedByInputWithMetadata(newValue, {
            disableTransactionBundling: true
          })
        : newValue
    );
  }

  updateRepeater(newValue: unknown) {
    this.emit(defensiveCopy(newValue));
  }

  handleChange(
    // https://github.com/SortableJS/Vue.Draggable#events
    movedElement:
      | {
          added: { newIndex: number; element: unknown };
        }
      | {
          removed: { oldIndex: number; element: unknown };
        }
      | { moved: { newIndex: number; oldIndex: number; element: unknown } }
  ) {
    if ("moved" in movedElement && this.expandedIndex !== -1) {
      this.expandedIndex = movedElement.moved.newIndex;
    }
  }

  addInBulk() {
    this.dialogOpen = true;
  }

  addItem() {
    const repeaterData = defensiveCopy(this.value) || [];

    if (this.options.forceDontProcessDefaultNewValueWhenAddingItemsHack) {
      repeaterData.push(this.defaultNewValue);
    } else {
      repeaterData.push(this.processDefaultValue(this.defaultNewValue));
    }

    if (!this.options.noExpand) {
      this.expandedIndex = repeaterData.length - 1;
    }

    this.emit(repeaterData);
  }

  onMediaSelected(medias: Media[]) {
    const repeaterData = defensiveCopy(this.value) || [];
    const path = this.options?.galleryMediaPath;

    if (!path) {
      NotificationLogger.log.message(
        "Could not bulk import media files. The Gallery Media Path is not set. Please check your Element settings."
      );
      return;
    }

    const items = medias.map(media => {
      return {
        ...this.processDefaultValue(this.defaultNewValue),
        [path]: media
      };
    });

    repeaterData.push(...items);
    this.emit(repeaterData);
  }

  add() {
    if (this.options?.galleryMode) {
      this.addInBulk();
    } else {
      this.addItem();
    }
  }

  maybeDeleteItem(index: number) {
    this.indexToDelete = index;

    if (this.options.deleteConfirm) {
      this.deleteDialogOpen = true;
    } else {
      this.deleteItem(index);
    }
  }

  cancelDeletion() {
    this.indexToDelete = -1;
    this.deleteDialogOpen = false;
  }

  deleteItem(index: number) {
    const repeaterData = defensiveCopy(this.value) || [];
    repeaterData.splice(index, 1);

    this.indexToDelete = -1;
    this.expandedIndex = -1;
    this.deleteDialogOpen = false;
    this.emit(repeaterData);
  }

  duplicateItem(index: number) {
    const repeaterData = defensiveCopy(this.value) || [];
    const itemToDuplicate = defensiveCopy(repeaterData[index]);
    const itemToDuplicateWithDefaultValues = {
      ...(itemToDuplicate as object),
      ...this.processDefaultValue(this.duplicateNewValue)
    };

    const startAfterItemToDuplicate = index + 1;
    repeaterData.splice(
      startAfterItemToDuplicate,
      0,
      itemToDuplicateWithDefaultValues
    );

    this.emit(repeaterData);
  }

  toggleDisabledItem(index: number) {
    const repeaterData = defensiveCopy(this.value) || [];
    // TODO: I wrote this in 10 seconds. It's bad. It's really bad.
    (repeaterData[index] as Record<string, any>).disabled = !(
      repeaterData[index] as Record<string, any>
    ).disabled;

    this.emit(repeaterData);
  }

  beforeDestroy() {
    uiStore.setPanelState({
      ...uiStore.panelState,
      oxyPanelIsMasked: false
    });
  }
}
